import { FailureReasonType, FailureStage, HelpKind } from "./task";
import {
  IWorkPipelineItem,
  IWorkPipelineUrgent,
  WorkPipelineBrandStatus,
  WorkPipelineStage,
} from "./work-pipeline";

/**
 * Template Pipeline
 * -----------------
 * One item per merchant (domain + geo) whose walk keeps failing. The stage is
 * what a person could do about it (see HelpKind); after someone helps, the item
 * waits in Verifying until the next run passes or fails.
 *
 * Testers work these from the Work Pipeline: an open item puts its merchant on
 * the board, and the board's claim and actions drive this service.
 */
export enum TemplatePipelineStage {
  /** Failed before runs recorded why; a retest classifies it. */
  NeedsTriage = "needs-triage",
  SignIn = "sign-in",
  BotWall = "bot-wall",
  TeachJourney = "teach-journey",
  TestProduct = "test-product",
  NotSuitable = "not-suitable",
  Template = "template",
  Infra = "infra",
  /** Help was given and a retest is queued or running. */
  Verifying = "verifying",
  Resolved = "resolved",
  Dismissed = "dismissed",
}

export const TEMPLATE_PIPELINE_OPEN_STAGES: TemplatePipelineStage[] = [
  TemplatePipelineStage.NeedsTriage,
  TemplatePipelineStage.SignIn,
  TemplatePipelineStage.BotWall,
  TemplatePipelineStage.TeachJourney,
  TemplatePipelineStage.TestProduct,
  TemplatePipelineStage.NotSuitable,
  TemplatePipelineStage.Template,
  TemplatePipelineStage.Infra,
  TemplatePipelineStage.Verifying,
];

export const TEMPLATE_PIPELINE_STAGE_ORDER: TemplatePipelineStage[] = [
  ...TEMPLATE_PIPELINE_OPEN_STAGES,
  TemplatePipelineStage.Resolved,
  TemplatePipelineStage.Dismissed,
];

export const TEMPLATE_PIPELINE_STAGE_META: Record<
  TemplatePipelineStage,
  { label: string; description: string; owner: "tester" | "developer" | "none" }
> = {
  [TemplatePipelineStage.NeedsTriage]: {
    label: "Needs triage",
    description:
      "Failed before runs recorded why. Retest it and the result sorts it into a queue.",
    owner: "tester",
  },
  [TemplatePipelineStage.SignIn]: {
    label: "Sign in",
    description:
      "Checkout sits behind a login. Sign in with Kindred's test account and capture the session with the extension.",
    owner: "tester",
  },
  [TemplatePipelineStage.BotWall]: {
    label: "Bot wall",
    description:
      "The site served a challenge page. Capture a session past it with the extension, or retest later.",
    owner: "tester",
  },
  [TemplatePipelineStage.TeachJourney]: {
    label: "Show the way",
    description:
      "The walk could not find its way to the coupon field. Note the product, cart URL and where the coupon field is, then retest.",
    owner: "tester",
  },
  [TemplatePipelineStage.TestProduct]: {
    label: "Test product",
    description:
      "Every product the walk reached was out of stock. Add a buyable product to the merchant, then retest.",
    owner: "tester",
  },
  [TemplatePipelineStage.NotSuitable]: {
    label: "Not suitable",
    description:
      "Classed as a site we don't test. Confirm it, or run it anyway if the classification is wrong.",
    owner: "tester",
  },
  [TemplatePipelineStage.Template]: {
    label: "Template broke",
    description:
      "The stored template or route stopped working. Rebuild or fix it with the extension; escalate to a dev only if the tool can't.",
    owner: "tester",
  },
  [TemplatePipelineStage.Infra]: {
    label: "Run errored",
    description:
      "Proxy, crash, timeout or AI limits. Check the site and retest; escalate to a dev only if it keeps erroring.",
    owner: "tester",
  },
  [TemplatePipelineStage.Verifying]: {
    label: "Verifying",
    description:
      "Help was given and a retest is queued. It resolves itself when the next run passes.",
    owner: "none",
  },
  [TemplatePipelineStage.Resolved]: {
    label: "Resolved",
    description: "A run has passed since it was raised.",
    owner: "none",
  },
  [TemplatePipelineStage.Dismissed]: {
    label: "Dismissed",
    description:
      "Closed by a person. Stays closed until a run fails for a different reason.",
    owner: "none",
  },
};

export const stageForHelpKind = (
  kind: HelpKind | null | undefined
): TemplatePipelineStage | null => {
  switch (kind) {
    case HelpKind.SIGN_IN:
      return TemplatePipelineStage.SignIn;
    case HelpKind.BOT_WALL:
      return TemplatePipelineStage.BotWall;
    case HelpKind.TEACH_JOURNEY:
      return TemplatePipelineStage.TeachJourney;
    case HelpKind.TEST_PRODUCT:
      return TemplatePipelineStage.TestProduct;
    case HelpKind.NOT_SUITABLE:
      return TemplatePipelineStage.NotSuitable;
    case HelpKind.TEMPLATE:
      return TemplatePipelineStage.Template;
    case HelpKind.INFRA:
      return TemplatePipelineStage.Infra;
    default:
      return null;
  }
};

export enum TemplatePipelineEventType {
  Raised = "raised",
  Failed = "failed",
  Claimed = "claimed",
  Released = "released",
  HelpProvided = "help-provided",
  RetestRequested = "retest-requested",
  RunAnyway = "run-anyway",
  Verified = "verified",
  StillFailing = "still-failing",
  RecoveredOnItsOwn = "recovered-on-its-own",
  Dismissed = "dismissed",
  Reopened = "reopened",
}

export interface ITemplatePipelineEvent {
  at: string;
  type: TemplatePipelineEventType;
  byId: string | null;
  byName: string | null;
  note: string | null;
  taskId: string | null;
  helpKind: HelpKind | null;
  failureStage: FailureStage | null;
}

/** Elements a person can point at in the extension's Help mode. */
export type TTemplatePipelinePick =
  | "addToCart"
  | "cartAccess"
  | "checkoutButton"
  | "couponToggle"
  | "couponField"
  | "couponApply"
  | "total"
  | "cookieAccept"
  | "newsletterClose"
  | "popupClose";

export const TEMPLATE_PIPELINE_PICK_LABELS: Record<TTemplatePipelinePick, string> = {
  addToCart: "Add to basket button",
  cartAccess: "Cart icon or link",
  checkoutButton: "Checkout button (cart → checkout)",
  couponToggle: "Coupon toggle (only if the field is hidden)",
  couponField: "Coupon code field",
  couponApply: "Apply button",
  total: "Order total",
  cookieAccept: "Accept cookies button",
  newsletterClose: "Close newsletter popup",
  popupClose: "Close other popup or region picker",
};

/** Page-level tags: "this page is…". */
export type TTemplatePipelinePageTag = "productUrl" | "cartUrl" | "checkoutUrl";

export const TEMPLATE_PIPELINE_PAGE_TAG_LABELS: Record<TTemplatePipelinePageTag, string> = {
  productUrl: "This page is the product page",
  cartUrl: "This page is the cart",
  checkoutUrl: "This page is the checkout (where a code goes)",
};

/** A variant option to choose before add to basket; one per variant group. */
export interface ITemplatePipelineVariant {
  selector: string;
  value: string | null;
}

/** The route a person showed in Help mode. Applied to the merchant before the retest. */
export interface ITemplatePipelineRoute {
  device: "mobile" | "desktop" | null;
  productUrl?: string | null;
  cartUrl?: string | null;
  checkoutUrl?: string | null;
  /** The coupon field is on the cart page itself. */
  couponOnCart?: boolean | null;
  selectors?: Partial<Record<TTemplatePipelinePick, string>>;
  /** Options to click before add to basket, in order (size, colour, …). */
  variants?: ITemplatePipelineVariant[];
  /** Pages the tab went through while Help mode was open, in order. */
  visitedUrls?: string[];
}

/** How a retest compared with the failure it was meant to fix. */
export type TTemplatePipelineProgress = "further" | "same" | "earlier" | null;

export interface ITemplatePipelineItem {
  _id: string;
  merchantId: string;
  merchantName: string | null;
  domain: string;
  countryCode: string;
  stage: TemplatePipelineStage;
  helpKind: HelpKind | null;
  errorType: FailureReasonType | string | null;
  errorMessage: string | null;
  failureStage: FailureStage | null;
  device: "mobile" | "desktop" | null;
  lastTaskId: string | null;
  firstFailedAt: string | null;
  lastFailedAt: string | null;
  /** Failures since it was last raised, resolved or reopened. */
  failureCount: number;
  stageSince: string;
  assignedTo: string | null;
  assignedToName: string | null;
  claimedAt: string | null;
  retestRequestedAt: string | null;
  /** Set when a retest failed: did it get further than before? */
  lastProgress: TTemplatePipelineProgress;
  /** The merchant's coupon count from its latest run; read live, not stored. */
  merchantCouponCount?: number | null;
  /** The last route shown in the extension's Help mode. */
  helpRoute: (ITemplatePipelineRoute & { at: string; byName: string | null }) | null;
  dismissedHelpKind: HelpKind | null;
  dismissReason: string | null;
  resolvedAt: string | null;
  events: ITemplatePipelineEvent[];
  urgent?: IWorkPipelineUrgent | null;
  /** The Work Pipeline row this merchant is worked from; its claim is the claim. */
  workPipelineRow?: ITemplatePipelineBoardRow | null;
  createdAt: string;
  updatedAt: string;
}

export interface ITemplatePipelineBoardRow {
  itemId: string;
  stage: WorkPipelineStage;
  assignedTo: string | null;
  assignedToName: string | null;
}

/** Help kinds that say the same thing as an existing Work Pipeline brand status. */
export const HELP_KIND_BRAND_STATUS: Partial<
  Record<HelpKind, WorkPipelineBrandStatus>
> = {
  [HelpKind.NOT_SUITABLE]: WorkPipelineBrandStatus.NotEcomm,
  [HelpKind.BOT_WALL]: WorkPipelineBrandStatus.AccessDenied,
};

/** Why the agent is stuck on a Work Pipeline row: the merchant's open template-pipeline item. */
export interface IWorkPipelineTemplateHelp {
  itemId: string;
  stage: TemplatePipelineStage;
  helpKind: HelpKind | null;
  errorType: string | null;
  errorMessage: string | null;
  failureStage: FailureStage | null;
  failureCount: number;
  lastFailedAt: string | null;
  lastProgress: TTemplatePipelineProgress;
}

/** What the extension's Help tab reports against for a domain + geo. */
export interface IWorkPipelineTemplateHelpContext {
  /** The board's row for the domain + geo; null when it isn't on the board. */
  row: IWorkPipelineItem | null;
  claimedByMe: boolean;
  /** Leads may act on a request someone else holds; the server checks the same. */
  isLead: boolean;
  /** The open request, or null when there is none. */
  templateHelp: ITemplatePipelineItem | null;
}

/** Help from the Work Pipeline; brandStatus is only read by dismiss. */
export interface IWorkPipelineTemplateHelpAction {
  note?: string;
  reason?: string;
  route?: ITemplatePipelineRoute | null;
  brandStatus?: WorkPipelineBrandStatus | null;
}

/** Teach a merchant by domain + geo, whether or not a request is open for it. */
export interface ITemplatePipelineTeachBody {
  domain: string;
  countryCode: string;
  note: string;
  route: ITemplatePipelineRoute;
}

export interface ITemplatePipelineListResponse {
  items: ITemplatePipelineItem[];
  total: number;
  page: number;
  limit: number;
  counts: Partial<Record<TemplatePipelineStage, number>>;
  /** Open items flagged urgent, under the same search and country. */
  urgentCount: number;
}
